/**
 * Seeded Random Number Generator for reproducible simulations
 * Uses xorshift128+ algorithm
 */

export class SeededRNG {
  private state: [number, number];

  constructor(seed: string) {
    const hash = this.hashString(seed);
    this.state = [hash >>> 0, (hash ^ 0xCAFEBABE) >>> 0];
    // Warm up the generator
    for (let i = 0; i < 10; i++) {
      this.next();
    }
  }

  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    return hash >>> 0;
  }

  /**
   * Generate next random number in [0, 1)
   */
  next(): number {
    let [s0, s1] = this.state;
    const result = (s0 + s1) >>> 0;

    s1 ^= s0;
    this.state = [
      ((s0 << 23) | (s0 >>> 9)) ^ s1 ^ (s1 << 14),
      (s1 << 28) | (s1 >>> 4)
    ];

    return result / 0xFFFFFFFF;
  }

  /**
   * Generate normally distributed random number using Box-Muller transform
   */
  nextGaussian(mean: number = 0, stdDev: number = 1): number {
    let u1 = this.next();
    let u2 = this.next();

    // Avoid log(0)
    while (u1 === 0) {
      u1 = this.next();
    }

    const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    return z0 * stdDev + mean;
  }
}

/**
 * Generate a random seed string
 */
export function generateRandomSeed(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}
